/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { func, object, string } from 'prop-types';

import { differenceInCalendarMonths, endOfWeek, isSameMonth } from 'date-fns';

// Material UI
import { Grid, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ChevronLeft, ChevronRight } from '@material-ui/icons';

import { useQueryClient } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { convertDateObjToStrDate, convertDateStringToObj, MonthsFullName } from '../../helpers/Calendar-Helper';
import { calendarColorStyle } from '../../../Helpers/Style-Helper';
import { getFeatureFlags } from '../../../../../../../../../state/ducks/MfeCheckout/ducks/App/App-Selectors';
import { trackEvent } from '../../../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles(
    (theme) => ({
        root: {
            margin: 0,
            padding: '24px 52px',
            [theme.breakpoints.down(490)]: {
                padding: '16px 30px',
            },
        },
        arrowIcon: {
            margin: 0,
            padding: 0,
            fontSize: '24px',
            color: calendarColorStyle.common?.arrows,
        },
        monthHeader: {
            color: calendarColorStyle.primary?.main,
            textAlign: 'center',
            fontSize: '1em',
            padding: '2px 4px',
            width: '100%',
        },
    }),
    { name: 'MonthNavegation' },
);

const MonthNavegation = ({
    calendarData,
    orderItem,
    recipientItem,
    startDateCalendar,
    setStartDateCalendar,
}) => {
    const queryClient = useQueryClient();
    const dispatchAction = useDispatch();

    const featureFlags = useSelector(getFeatureFlags);
    const leadTimeEnterpriseCalendarEnabled = featureFlags['which-lead-time-enterprise-calendar-enabled'] || 0; // default 5 min
    const isNewDeliveryCalendaV2CheckoutEnabled = featureFlags['is-new-delivery-calendar-checkout-enabled'];
    const isStandaloneCard = ['GREETINGCARD-ITEM'].includes(orderItem.itemInfo.lineItemType);

    const classes = useStyles();

    const currentDate = new Date();
    const startDate = convertDateStringToObj(startDateCalendar);
    const lastDateDateOnCalendar = calendarData.calendarDates[calendarData.calendarDates.length - 1];
    const lastDeliveryDateCalendar = convertDateStringToObj(lastDateDateOnCalendar.deliveryDate);
    const startDateFromCalendarResponse = convertDateStringToObj(calendarData.calendarDates[0].deliveryDate);

    let currentMonth = startDate.getMonth();

    // set the name of the current month is showing
    const startDateEndOfWeek = endOfWeek(startDateFromCalendarResponse);
    if (!isSameMonth(startDateEndOfWeek, startDate)) {
        const endOfMonthCalendar = new Date(startDate.getFullYear(), startDate.getMonth() + 2, 0);
        currentMonth = endOfMonthCalendar.getMonth();
    }
    const nameOfTheMonth = MonthsFullName[parseInt(currentMonth, 10)];

    let calendarPresentationFamily = 'food';
    if (
        (calendarPresentationFamily === 'flower' && recipientItem.zipCode)
        || (isNewDeliveryCalendaV2CheckoutEnabled && orderItem.displayCalendar)
        || isStandaloneCard
    ) {
        calendarPresentationFamily = 'flower';
    }
    const handleGetNextMonth = () => {
        const objTempDate = lastDeliveryDateCalendar;
        objTempDate.setDate(1);

        // The date is reset to 1 before the month is changed to prevent skipping months.
        // When going from months with 31 days to 30.
        objTempDate.setMonth(objTempDate.getMonth());
        const objNextMonthStartDate = new Date(objTempDate.getFullYear(), objTempDate.getMonth(), 1);
        const nextDate = convertDateObjToStrDate(objNextMonthStartDate);

        if (leadTimeEnterpriseCalendarEnabled === 0) {
            queryClient.invalidateQueries([`${calendarPresentationFamily}EnterpriseCalendar-${nextDate}`, { type: 'done' }]);
        }
        dispatchAction(
            trackEvent({
                eventName: 'feature_interaction',
                eventCategory: 'Checkout',
                eventAction: 'Calendar',
                eventLabel: 'Next Month Click',
            }),
        );
        setStartDateCalendar(nextDate);
    };

    const handleGetPrevMonth = () => {
        const objTempDate = startDate;
        objTempDate.setMonth(objTempDate.getMonth() - 1);
        const objPrevMonthStartDate = new Date(objTempDate.getFullYear(), objTempDate.getMonth(), 1);
        const strPrevMonthStartDate = convertDateObjToStrDate(objPrevMonthStartDate);

        if (leadTimeEnterpriseCalendarEnabled === 0) {
            queryClient.invalidateQueries([`${calendarPresentationFamily}EnterpriseCalendar-${strPrevMonthStartDate}`, { type: 'done' }]);
        }

        dispatchAction(
            trackEvent({
                eventName: 'feature_interaction',
                eventCategory: 'Checkout',
                eventAction: 'Calendar',
                eventLabel: 'Previous Month Click',
            }),
        );

        setStartDateCalendar(strPrevMonthStartDate);
    };

    return (
        <Grid id="month-calendar-navegation" container className={classes.root}>
            <Grid item xs={1}>
                <Grid container>
                    {
                        differenceInCalendarMonths(startDate, currentDate) > 0
                        && (
                            <IconButton
                                id="enterprise-calendar-previous-button"
                                aria-label="Previous Month"
                                onClick={handleGetPrevMonth}
                                className={classes.arrowIcon}
                                data-test="enterprise-calendar-previous-button"
                            >
                                <ChevronLeft />
                            </IconButton>
                        )
                    }
                </Grid>
            </Grid>
            <Grid item xs={10} className={classes.monthHeader}>
                {nameOfTheMonth} {startDate.getFullYear()}
            </Grid>
            <Grid item xs={1}>
                <Grid container justifyContent="flex-end">
                    {
                        !isSameMonth(startDate, lastDeliveryDateCalendar)
                        && (
                            <IconButton
                                id="enterprise-calendar-next-button"
                                aria-label="Next Month"
                                onClick={handleGetNextMonth}
                                data-test="enterprise-calendar-next-button"
                                className={classes.arrowIcon}
                            >
                                <ChevronRight />
                            </IconButton>
                        )
                    }
                </Grid>
            </Grid>
        </Grid>
    );
};

MonthNavegation.propTypes = {
    calendarData: object.isRequired,
    orderItem: object.isRequired,
    recipientItem: object.isRequired,
    setStartDateCalendar: func.isRequired,
    startDateCalendar: string.isRequired,
};

export default MonthNavegation;
